import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  CheckCircle2, XCircle, AlertCircle, PauseCircle, Clock,
  Copy, ArrowRight, ArrowLeft,
  Shield, Building2, Users, Target, PoundSterling, AlertTriangle,
  Sparkles, Mail
} from 'lucide-react';
import { toast } from 'sonner';

// Sleek hint indicator component
const HintIndicator = ({ hint }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center justify-center w-4 h-4 ml-1.5 cursor-help group">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1e3a5f]/40 opacity-75 group-hover:opacity-100"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1e3a5f]/60 group-hover:bg-[#1e3a5f] transition-colors"></span>
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="bg-[#1e3a5f] text-white px-3 py-2 rounded-lg text-xs max-w-[250px] shadow-lg border-0"
      >
        <p>{hint}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Step explanations
const STEP_HINTS = {
  1: "Market Fit assesses if this project type aligns with your primary or secondary markets (Commercial/Residential).",
  2: "Client Type checks if the client has authority to instruct work and is an approved type (Contractor, PM, QS, Developer, Landlord, Architect).",
  3: "Project Clarity ensures you have enough information: programme timeline, scope definition, and decision maker identified.",
  4: "Scale & Value verifies the project meets minimum thresholds for construction value and estimated fee.",
  5: "Delivery Risk evaluates programme, payment, scope, client, and resource risks to assess viability.",
  6: "Strategic Value considers factors like new sector entry, key client relationship, and portfolio value."
};

// Residential criteria explanations  
const RESIDENTIAL_HINTS = {
  client_quality_score: "Rating 1-10 based on client reputation, payment history, and ease of working relationship.",
  has_strategic_value: "Does this project offer strategic benefits like market expansion or portfolio enhancement?",
  delivery_risk_manageable: "Are the delivery risks (programme, scope, resources) within acceptable bounds?",
  alignment_with_strengths: "Does this project type match your company's defined MEP expertise areas? Configure in Admin → Strengths.",
  meets_scale_threshold: "Does the project meet minimum construction value requirements?"
};

const RISK_LEVELS = [
  { id: 'low', label: 'Low', color: 'text-emerald-600' },
  { id: 'medium', label: 'Medium', color: 'text-amber-600' },
  { id: 'high', label: 'High', color: 'text-red-600' },
];

const STRATEGIC_FACTORS = [
  { id: 'new_sector_entry', label: 'New Sector Entry', description: 'Opens doors to a new market' },
  { id: 'key_client_relationship', label: 'Key Client Relationship', description: 'Important client to nurture' },
  { id: 'portfolio_piece', label: 'Portfolio Piece', description: 'Showcase project for marketing' },
  { id: 'repeat_business_potential', label: 'Repeat Business', description: 'High likelihood of future work' },
  { id: 'team_development_opportunity', label: 'Team Development', description: 'Learning opportunity for staff' },
  { id: 'geographic_expansion', label: 'Geographic Expansion', description: 'Expands our regional presence' },
];

const OUTCOME_CONFIG = {
  quote: {
    icon: CheckCircle2,
    color: 'bg-emerald-500',
    textColor: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    label: 'Quote',
    description: 'Proceed to create and send a quote'
  },
  quote_approval_required: {
    icon: Clock,
    color: 'bg-sky-500',
    textColor: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    label: 'Quote (Approval Required)',
    description: 'Create quote but requires Director approval before sending'
  },
  do_not_quote: {
    icon: XCircle,
    color: 'bg-red-500',
    textColor: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Do Not Quote',
    description: 'Decline this opportunity'
  },
  do_not_quote_yet: {
    icon: AlertCircle,
    color: 'bg-amber-500',
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'Do Not Quote Yet',
    description: 'Missing information - gather more details and re-run'
  },
  pause: {
    icon: PauseCircle,
    color: 'bg-slate-500',
    textColor: 'text-slate-700',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    label: 'Pause',
    description: 'Needs investigation or clarification'
  }
};

const STEP_ICONS = {
  1: Building2,
  2: Users,
  3: Target,
  4: PoundSterling,
  5: AlertTriangle,
  6: Sparkles,
};

export const DecisionOutcomePage = () => {
  const { enquiryId } = useParams();
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  
  const [enquiry, setEnquiry] = useState(null);
  const [decision, setDecision] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [running, setRunning] = useState(false);
  const [requesting, setRequesting] = useState(false);
  
  // Risk and strategic inputs for running decision
  const [showInputs, setShowInputs] = useState(false);
  const [deliveryRisk, setDeliveryRisk] = useState({
    programme: 'low',
    payment: 'low',
    scope: 'low',
    client: 'low',
    resource: 'low',
  });
  const [strategicValue, setStrategicValue] = useState({
    new_sector_entry: false,
    key_client_relationship: false,
    portfolio_piece: false,
    repeat_business_potential: false,
    team_development_opportunity: false,
    geographic_expansion: false,
  });
  
  // Next actions checklist
  const [nextActions, setNextActions] = useState([]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryId]);

  const loadData = async () => {
    try {
      setLoadError(null);
      
      // Load enquiry
      const enquiryRes = await authFetch(`/enquiries/${enquiryId}`);
      if (enquiryRes.ok) {
        const data = await enquiryRes.json();
        setEnquiry(data);
      } else {
        const errData = await enquiryRes.json().catch(() => ({}));
        setLoadError(`Failed to load enquiry: ${errData.detail || enquiryRes.status}`);
        setLoading(false);
        return;
      }
      
      // Try to load existing decision
      const decisionRes = await authFetch(`/decisions/enquiry/${enquiryId}`);
      if (decisionRes.ok) {
        const data = await decisionRes.json();
        setDecision(data);
        
        // Load suggested scripts
        const scriptsRes = await authFetch(`/scripts/suggested/${data.outcome}`);
        if (scriptsRes.ok) {
          const scriptsData = await scriptsRes.json();
          setScripts(scriptsData.scripts || []);
        }
        
        // Set next actions based on outcome
        setNextActions(getNextActions(data.outcome));
      } else {
        // No decision yet - show inputs
        setShowInputs(true);
      }
    } catch (err) {
      console.error('Load error:', err);
      setLoadError(`Error: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getNextActions = (outcome) => {
    switch (outcome) {
      case 'quote':
        return [
          { id: 'create_quote', label: 'Create Quote', completed: false },
          { id: 'review_scope', label: 'Review scope with team', completed: false },
          { id: 'send_quote', label: 'Send quote to client', completed: false },
          { id: 'schedule_followup', label: 'Schedule follow-up call', completed: false },
        ];
      case 'quote_approval_required':
        return [
          { id: 'create_quote', label: 'Create draft quote', completed: false },
          { id: 'request_approval', label: 'Request Director approval', completed: false },
          { id: 'await_approval', label: 'Await approval decision', completed: false },
          { id: 'send_quote', label: 'Send quote once approved', completed: false },
        ];
      case 'do_not_quote':
        return [
          { id: 'inform_client', label: 'Inform client politely', completed: false },
          { id: 'archive_enquiry', label: 'Archive enquiry', completed: false },
          { id: 'log_reason', label: 'Log decline reason', completed: false },
        ];
      case 'do_not_quote_yet':
        return [
          { id: 'identify_gaps', label: 'Identify missing information', completed: false },
          { id: 'contact_client', label: 'Contact client for details', completed: false },
          { id: 'update_enquiry', label: 'Update enquiry data', completed: false },
          { id: 'rerun_decision', label: 'Re-run decision engine', completed: false },
        ];
      case 'pause':
        return [
          { id: 'investigate', label: 'Investigate concerns', completed: false },
          { id: 'discuss_team', label: 'Discuss with team', completed: false },
          { id: 'gather_info', label: 'Gather additional info', completed: false },
          { id: 'rerun_decision', label: 'Re-run or request override', completed: false },
        ];
      default:
        return [];
    }
  };

  const runDecision = async () => {
    setRunning(true);
    try {
      const res = await authFetch('/decisions/run', {
        method: 'POST',
        body: {
          enquiry_id: enquiryId,
          delivery_risk: deliveryRisk,
          strategic_value: strategicValue,
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDecision(data);
        setShowInputs(false);
        setNextActions(getNextActions(data.outcome));
        
        // Load suggested scripts
        const scriptsRes = await authFetch(`/scripts/suggested/${data.outcome}`);
        if (scriptsRes.ok) {
          const scriptsData = await scriptsRes.json();
          setScripts(scriptsData.scripts || []);
        }
        
        toast.success('Decision complete');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to run decision');
      }
    } catch (err) {
      toast.error('Failed to run decision');
    } finally {
      setRunning(false);
    }
  };

  const requestApproval = async () => {
    setRequesting(true);
    try {
      const res = await authFetch('/approvals/request', {
        method: 'POST',
        body: {
          entity_type: 'decision_result',
          entity_id: decision.id,
          request_type: 'quote_approval'
        }
      });
      
      if (res.ok) {
        toast.success('Approval requested');
        // Update next actions
        setNextActions(prev => prev.map(a => 
          a.id === 'request_approval' ? { ...a, completed: true } : a
        ));
      }
    } catch (err) {
      toast.error('Failed to request approval');
    } finally {
      setRequesting(false);
    }
  };

  const copyScript = async (script) => {
    try {
      await navigator.clipboard.writeText(script.content);
      toast.success('Copied to clipboard');
      
      // Log usage
      await authFetch(`/scripts/${script.id}/use`, { method: 'POST' });
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const openEmailDraft = (script) => {
    const subject = encodeURIComponent(`Re: ${enquiry?.project_name || 'Project Enquiry'}`);
    const body = encodeURIComponent(script.content
      .replace(/\{\{client_name\}\}/g, enquiry?.client_name || '[Client Name]')
      .replace(/\{\{project_name\}\}/g, enquiry?.project_name || '[Project Name]')
      .replace(/\{\{user_name\}\}/g, `${user?.first_name} ${user?.last_name}`)
    );
    window.open(`mailto:${enquiry?.client_email || ''}?subject=${subject}&body=${body}`);
    
    // Log action
    authFetch('/actions/log', {
      method: 'POST',
      body: { action_type: 'email_draft_opened', entity_type: 'template_script', entity_id: script.id }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  // Handle error state - enquiry not found or failed to load
  if (!enquiry || loadError) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-700 font-medium mb-2">Unable to load enquiry</p>
          <p className="text-slate-500 text-sm mb-4">{loadError || 'Enquiry data could not be loaded'}</p>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Show input form if no decision yet
  if (showInputs && !decision) {
    return (
      <div className="min-h-screen bg-[#f8fafc]" data-testid="decision-inputs-page">
        <header className="bg-white border-b border-slate-100 px-6 md:px-10 py-6">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Run Decision Engine
            </h1>
            <p className="text-slate-500 mt-1">
              {enquiry?.project_name} • {enquiry?.reference_number}
            </p>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-10 py-6 md:py-8">
          {/* Delivery Risk Assessment */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-[#1e3a5f] mb-4 md:mb-6">
              Delivery Risk Assessment
            </h2>
            
            <div className="space-y-4">
              {['programme', 'payment', 'scope', 'client', 'resource'].map((risk) => (
                <div key={risk} className="p-3 md:p-4 rounded-xl bg-slate-50">
                  <span className="font-medium text-slate-700 capitalize block mb-3 text-sm md:text-base">
                    {risk.charAt(0).toUpperCase() + risk.slice(1)} Risk
                  </span>
                  <div className="grid grid-cols-3 gap-2">
                    {RISK_LEVELS.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        onClick={() => setDeliveryRisk(prev => ({ ...prev, [risk]: level.id }))}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          deliveryRisk[risk] === level.id
                            ? level.id === 'low' 
                              ? 'bg-emerald-500 text-white ring-2 ring-emerald-500 ring-offset-1'
                              : level.id === 'medium'
                                ? 'bg-amber-500 text-white ring-2 ring-amber-500 ring-offset-1'
                                : 'bg-red-500 text-white ring-2 ring-red-500 ring-offset-1'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid={`risk-${risk}-${level.id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${
                          deliveryRisk[risk] === level.id 
                            ? 'bg-white' 
                            : level.id === 'low' 
                              ? 'bg-emerald-500' 
                              : level.id === 'medium' 
                                ? 'bg-amber-500' 
                                : 'bg-red-500'
                        }`} />
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Strategic Value */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 mb-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-[#1e3a5f] mb-4 md:mb-6">
              Strategic Value Factors
            </h2>
            <p className="text-slate-500 text-sm mb-4">
              Tick any factors that apply. If none, approval will be required.
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {STRATEGIC_FACTORS.map((factor) => (
                <div 
                  key={factor.id}
                  className="flex items-start gap-3 p-3 md:p-4 rounded-xl bg-slate-50 border border-slate-200"
                >
                  <Checkbox
                    id={factor.id}
                    checked={strategicValue[factor.id]}
                    onCheckedChange={(checked) => setStrategicValue(prev => ({ ...prev, [factor.id]: checked }))}
                    className="mt-0.5 flex-shrink-0"
                    data-testid={`strategic-${factor.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <Label htmlFor={factor.id} className="font-medium text-slate-700 cursor-pointer text-sm md:text-base">
                      {factor.label}
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">{factor.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <Button
            onClick={runDecision}
            disabled={running}
            className="w-full h-14 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold text-lg"
            data-testid="run-decision-btn"
          >
            {running ? 'Running Decision Engine...' : 'Run Decision Engine'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </main>
      </div>
    );
  }

  const outcomeConfig = OUTCOME_CONFIG[decision?.outcome] || OUTCOME_CONFIG.pause;
  const OutcomeIcon = outcomeConfig.icon;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="decision-outcome-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 md:px-10 py-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Today
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <span>{enquiry?.reference_number}</span>
            <span>•</span>
            <span className="capitalize">
              {enquiry?.sector === 'other' && enquiry?.sector_custom_name 
                ? `Other (${enquiry.sector_custom_name})`
                : enquiry?.sector?.replace('_', ' ')}
            </span>
            <span>•</span>
            <span className="uppercase">{enquiry?.project_type?.replace('_', ' ')}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
            {enquiry?.project_name}
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-8 space-y-6">
        {/* Outcome Banner */}
        <div 
          className={`${outcomeConfig.bgColor} ${outcomeConfig.borderColor} border-2 rounded-2xl p-6 md:p-8 animate-fade-in`}
          data-testid="outcome-banner"
        >
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 ${outcomeConfig.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
              <OutcomeIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h2 className={`font-heading text-2xl font-bold ${outcomeConfig.textColor} mb-2`}>
                {outcomeConfig.label}
              </h2>
              <p className="text-slate-600">{outcomeConfig.description}</p>
              
              {decision?.is_override && (
                <div className="mt-4 p-3 bg-white/50 rounded-xl">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Director Override:</span> {decision.override_reason}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Original outcome: {decision.original_outcome?.replace('_', ' ')}
                  </p>
                </div>
              )}
            </div>
            
            {decision?.can_create_quote && (
              <div className="chip chip-success">
                Can create quote
              </div>
            )}
          </div>
        </div>

        {/* Approval Request Banner */}
        {decision?.requires_director_approval && user?.role !== 'director' && (
          <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-6" data-testid="approval-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-sky-600" />
                <div>
                  <h3 className="font-semibold text-sky-800">Director Approval Required</h3>
                  <p className="text-sm text-sky-600">This quote needs Director sign-off before sending</p>
                </div>
              </div>
              <Button
                onClick={requestApproval}
                disabled={requesting}
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full"
                data-testid="request-approval-btn"
              >
                {requesting ? 'Requesting...' : 'Request Approval'}
              </Button>
            </div>
          </div>
        )}

        {/* Step Results Accordion */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Decision Steps</h3>
          </div>
          
          <Accordion type="single" collapsible className="divide-y divide-slate-100">
            {decision?.step_results?.map((step, idx) => {
              const StepIcon = STEP_ICONS[step.step_number] || Target;
              const passed = step.passed;
              
              return (
                <AccordionItem key={idx} value={`step-${idx}`} className="border-0">
                  <AccordionTrigger 
                    className="px-6 py-4 hover:bg-slate-50 hover:no-underline"
                    data-testid={`step-trigger-${step.step_number}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        passed ? 'bg-emerald-100' : step.outcome ? 'bg-red-100' : 'bg-slate-100'
                      }`}>
                        <StepIcon className={`w-5 h-5 ${
                          passed ? 'text-emerald-600' : step.outcome ? 'text-red-600' : 'text-slate-600'
                        }`} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-slate-700 flex items-center">
                          Step {step.step_number}: {step.step_name}
                          <HintIndicator hint={STEP_HINTS[step.step_number] || `Evaluates ${step.step_name.toLowerCase()} criteria.`} />
                        </p>
                        <p className="text-sm text-slate-500">{step.reason}</p>
                      </div>
                      {passed ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : step.outcome ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : null}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <div className="ml-14 bg-slate-50 rounded-xl p-4">
                      <pre className="text-sm text-slate-600 whitespace-pre-wrap font-mono">
                        {JSON.stringify(step.details, null, 2)}
                      </pre>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Residential Result */}
        {decision?.residential_result && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-amber-800 mb-4 flex items-center">
              Residential Selection Result
              <HintIndicator hint="Residential projects require a separate assessment. 3 out of 5 criteria must be met to proceed." />
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-amber-700">Criteria Met</p>
                <p className="text-2xl font-bold text-amber-800">
                  {decision.residential_result.criteria_met} / 5
                </p>
              </div>
              <div>
                <p className="text-sm text-amber-700">Required</p>
                <p className="text-2xl font-bold text-amber-800">3</p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {Object.entries(decision.residential_result.criteria_breakdown || {}).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  {value ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm text-amber-800 capitalize flex items-center">
                    {key.replace(/_/g, ' ')}
                    {RESIDENTIAL_HINTS[key] && (
                      <HintIndicator hint={RESIDENTIAL_HINTS[key]} />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Actions Checklist */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-[#1e3a5f]">Next Actions</h3>
          </div>
          <div className="p-4 space-y-2">
            {nextActions.map((action) => (
              <div 
                key={action.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <Checkbox
                  checked={action.completed}
                  onCheckedChange={(checked) => {
                    setNextActions(prev => prev.map(a => 
                      a.id === action.id ? { ...a, completed: checked } : a
                    ));
                    // Log action
                    authFetch('/actions/log', {
                      method: 'POST',
                      body: { 
                        action_type: checked ? 'action_completed' : 'action_uncompleted', 
                        entity_type: 'next_action',
                        details: { action_id: action.id, action_label: action.label }
                      }
                    });
                  }}
                  data-testid={`action-${action.id}`}
                />
                <span className={`flex-1 text-sm ${action.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                  {action.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Suggested Scripts */}
        {scripts.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-[#1e3a5f]">Suggested Scripts</h3>
            </div>
            <div className="p-4 space-y-4">
              {scripts.map((script) => (
                <div 
                  key={script.id}
                  className="border border-slate-200 rounded-xl overflow-hidden"
                  data-testid={`script-${script.id}`}
                >
                  <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-700">{script.title}</h4>
                      <span className="chip chip-default capitalize mt-1">
                        {script.category.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyScript(script)}
                        className="rounded-full"
                        data-testid={`copy-script-${script.id}`}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                      {script.category.includes('email') || script.category === 'follow_up' || script.category === 'closing' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEmailDraft(script)}
                          className="rounded-full"
                          data-testid={`email-script-${script.id}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Open Email
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div className="p-4">
                    <pre className="text-sm text-slate-600 whitespace-pre-wrap font-body">
                      {script.content}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Engine Info */}
        <div className="bg-slate-100 rounded-xl p-4 flex items-center justify-between text-sm text-slate-500">
          <span>Decision Engine v{decision?.engine_version}</span>
          <span>
            {decision?.decided_at && new Date(decision.decided_at).toLocaleString('en-GB')}
          </span>
        </div>
      </main>
    </div>
  );
};
